/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Calls of the material control service.
 *
 * The listings of the three modules are read with the simple search of the
 * advanced search service, so this file keeps the detail of a material control
 * and the actions registered on it.
 */
import {createAsyncThunk} from "@reduxjs/toolkit";
import {apiInstance} from "../../Apis/Axios";
import {MATERIAL_CONTROL_SERVICE} from "../EndPoints";

/**
 * Reads the detail of a material control, with every one of its blocks.
 */
export const GetMaterialControlById = createAsyncThunk(
    'materialControlService/getMaterialControlById',
    async (id) => {
        const result = await apiInstance
            .get(`${MATERIAL_CONTROL_SERVICE.getMaterialControlById}${id}`)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = result;

        return {data, status};
    },
);

/**
 * Registers a new material control with every one of its blocks.
 */
export const CreateMaterialControl = createAsyncThunk(
    'materialControlService/createMaterialControl',
    async (params) => {
        const response = await apiInstance
            .post(`${MATERIAL_CONTROL_SERVICE.createMaterialControl}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Registers the answer of an approver: approving, rejecting or asking the
 * requester for changes.
 */
export const ApprovalMaterialControl = createAsyncThunk(
    'materialControlService/approvalMaterialControl',
    async (params) => {
        const response = await apiInstance
            .put(`${MATERIAL_CONTROL_SERVICE.approvalAction}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Registers a gate movement with the photos taken by the guard.
 */
export const RegisterGateEvent = createAsyncThunk(
    'materialControlService/registerGateEvent',
    async (params) => {
        const response = await apiInstance
            .post(`${MATERIAL_CONTROL_SERVICE.registerGateEvent}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Registers the validation the guard gives to a complete item at the gate.
 */
export const ValidateItemComplete = createAsyncThunk(
    'materialControlService/validateItemComplete',
    async (params) => {
        const response = await apiInstance
            .put(`${MATERIAL_CONTROL_SERVICE.validateItemComplete}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Registers the packaging quantities the guard counted at the gate.
 *
 * The gate counts every packaging of the folio, so all of them travel in the
 * same call, each one with the record it was registered in.
 */
export const RegisterActualQuantities = createAsyncThunk(
    'materialControlService/registerActualQuantities',
    async (params) => {
        const response = await apiInstance
            .put(`${MATERIAL_CONTROL_SERVICE.registerActualQuantities}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Raises a discrepancy report against a material control.
 */
export const CreateMaterialControlReport = createAsyncThunk(
    'materialControlService/createMaterialControlReport',
    async (params) => {
        const response = await apiInstance
            .post(`${MATERIAL_CONTROL_SERVICE.createReport}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Registers the materials the gate writes for a material control, the ones that
 * came into the plant or left it as the guard captured them.
 */
export const RegisterMaterialControlItemsBasic = createAsyncThunk(
    'materialControlService/registerMaterialControlItemsBasic',
    async ({id, body}) => {
        const response = await apiInstance
            .post(`${MATERIAL_CONTROL_SERVICE.registerItemsBasic}${id}`, body)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Updates the transport block of a material control, the vehicle and driver
 * data the guard confirms at the gate.
 */
/**
 * Registers the thermo trailers the truck arrives at the gate with, the ones the
 * guard completes when the requester left them blank.
 *
 * The truck pulls more than one trailer, so all of them travel in the same call
 * and the service writes them together.
 */
export const RegisterMaterialControlTrailers = createAsyncThunk(
    'materialControlService/registerMaterialControlTrailers',
    async (params) => {
        const response = await apiInstance
            .put(`${MATERIAL_CONTROL_SERVICE.registerTrailers}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

export const UpdateMaterialControlTransport = createAsyncThunk(
    'materialControlService/updateMaterialControlTransport',
    async (params) => {
        const response = await apiInstance
            .put(`${MATERIAL_CONTROL_SERVICE.updateTransport}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Registers the values of the movement the gate completes on a folio — its
 * date, platforms, observations and notes — in a single update. Every value
 * travels with it, so the ones the folio already carries are sent back as they
 * were and only the ones the requester left blank are written by the guard.
 */
export const UpdateMaterialControlMovementValues = createAsyncThunk(
    'materialControlService/updateMaterialControlMovementValues',
    async (params) => {
        const response = await apiInstance
            .put(`${MATERIAL_CONTROL_SERVICE.updateMovementValues}`, params)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);

/**
 * Reads the QR code body of a material control, the one the vehicle is
 * identified with at the gate. The service only issues it while the folio is
 * approved and the gate still has a movement to register on it.
 */
export const GetMaterialControlQrCode = createAsyncThunk(
    'materialControlService/getMaterialControlQrCode',
    async (id) => {
        const response = await apiInstance
            .get(`${MATERIAL_CONTROL_SERVICE.getMaterialControlQrCode}${id}`)
            .then(function (response) {
                return response;
            })
            .catch(function (error) {
                return error.response;
            });

        const {data, status} = response;

        return {data, status};
    },
);
